import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog as MuiDialog } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ReactComponent as ArrowLeft } from '../../assets/arrow-left.svg';
import { ReactComponent as ArrowRight } from '../../assets/arrow-right-2.svg';
import Button, { LoadingButton } from '../Button';
import Dialog from '../Dialog/Dialog';
import { Flex } from '../Flex';
import IconButton from '../IconButton';
import { fontSize, space } from '../../style/utils';
import WizardStepper from './WizardStepper';

const Wrapper = styled(MuiDialog)`
  & .MuiDialog-paper {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

const Header = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: ${space('xxl')};
`;

const Footer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: ${space('xxl')};
  height: 100px;
  gap: ${space('xxl')};
  border-top: 1px solid ${({ theme }) => theme.palette.border.selected};
`;

const FooterRight = styled(Flex)`
  align-items: center;
  gap: ${space('lg')};
  margin-left: auto;
`;

const Title = styled(Typography)`
  font-size: ${fontSize('xxl')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const Content = styled.div`
  height: calc(100% - 252px);
  overflow-y: auto;
`;

const ButtonIconWrapper = styled(Flex)<{ $disabled: boolean }>`
  svg {
    path {
      fill: ${({ $disabled, theme }) =>
        $disabled ? theme.palette.icon.disabled : 'auto'};
      transition: fill 0.3s;
    }
  }
`;

export type WizardStep = {
  title: string;
  description?: string;
  content: ReactNode;
  tooltipMessage?: string;
  isValid?: boolean | (() => boolean);
  footerRight?: ReactNode;
};

type Props = {
  isOpen: boolean;
  title: string;
  steps: WizardStep[];
  onClose: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
  showConfirmOnClose?: boolean;
  confirmCloseMessage?: string;
  submitButtonText?: string;
  nextButtonText?: string;
  backButtonText?: string;
  submitButtonIcon?: ReactNode;
  submitButtonIconPos?: 'start' | 'end';
};

const Wizard = ({
  isOpen,
  title,
  steps,
  onClose,
  onSubmit,
  isLoading = false,
  showConfirmOnClose = true,
  confirmCloseMessage = 'Selections made will not be saved',
  submitButtonText = 'Submit',
  nextButtonText = 'Next',
  backButtonText = 'Back',
  submitButtonIcon,
  submitButtonIconPos = 'end',
}: Props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);

  const isLastStep = currentStep === steps.length - 1;
  const currentStepData = steps[currentStep];
  const isCurrentStepValid =
    typeof currentStepData?.isValid === 'function'
      ? currentStepData.isValid()
      : currentStepData?.isValid !== false;

  const handleClose = () => {
    setShowCloseConfirmDialog(false);
    onClose();
    setCurrentStep(0);
  };

  const handleCloseClick = () => {
    if (showConfirmOnClose) {
      setShowCloseConfirmDialog(true);
    } else {
      handleClose();
    }
  };

  const handleNextClick = async () => {
    if (isLastStep) {
      onSubmit();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBackClick = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      setCurrentStep(index);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  return (
    <Wrapper open={isOpen} maxWidth={false}>
      <Header>
        <Title>{title}</Title>
        <IconButton onClick={handleCloseClick}>
          <CloseIcon />
        </IconButton>
      </Header>

      <WizardStepper
        steps={steps.map((step) => ({
          title: step.title,
          description: step.description,
        }))}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      <Content>{currentStepData?.content}</Content>

      <Footer>
        {currentStep > 0 && (
          <Button
            variant="outlined"
            size="medium"
            onClick={handleBackClick}
            startIcon={
              <ButtonIconWrapper $disabled={false}>
                <ArrowLeft />
              </ButtonIconWrapper>
            }
          >
            {backButtonText}
          </Button>
        )}
        <FooterRight>
          {currentStepData?.footerRight}
          <Tooltip
            title={!isCurrentStepValid ? currentStepData?.tooltipMessage : ''}
          >
            <div>
              <LoadingButton
                size="medium"
                disabled={!isCurrentStepValid}
                loading={isLoading}
                onClick={handleNextClick}
                endIcon={
                  submitButtonIconPos === 'end' ? (
                    <ButtonIconWrapper $disabled={!isCurrentStepValid}>
                      {isLastStep && !!submitButtonIcon ? (
                        submitButtonIcon
                      ) : (
                        <ArrowRight />
                      )}
                    </ButtonIconWrapper>
                  ) : undefined
                }
                startIcon={
                  submitButtonIconPos === 'start' ? (
                    <ButtonIconWrapper $disabled={!isCurrentStepValid}>
                      {isLastStep && !!submitButtonIcon ? (
                        submitButtonIcon
                      ) : (
                        <ArrowRight />
                      )}
                    </ButtonIconWrapper>
                  ) : undefined
                }
              >
                {isLastStep ? submitButtonText : nextButtonText}
              </LoadingButton>
            </div>
          </Tooltip>
        </FooterRight>
      </Footer>

      <Dialog
        open={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
        onConfirmButtonClick={handleClose}
        isLoading={false}
        title={'Exit Wizard'}
        message="Are you sure you want to exit?"
        secondaryMessage={confirmCloseMessage}
        cancelText="Return"
        confirmText="Exit"
      />
    </Wrapper>
  );
};

export default Wizard;
